import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Tag } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Card } from "@/pages/admin/panel/ui";

const input = "w-full border border-zinc-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-sky-400";

export function DiscountCodesCard({ certs }) {
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/discount-codes").then((r) => setCodes(r.data.codes)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const edit = (i, patch) => setCodes(codes.map((c, k) => (k === i ? { ...c, ...patch } : c)));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/discount-codes", { codes: codes.filter((c) => c.code.trim()).map((c) => ({
        code: c.code.trim(), amount_usd: +c.amount_usd || 0, stripe_coupon_id: c.stripe_coupon_id || "",
        cert_slug: c.cert_slug || null, active: !!c.active })) });
      setCodes(data.codes);
      toast.success("Discount codes saved — checkout, drip emails and tickers use them from now on");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (!codes) return null;
  return (
    <Card icon={Tag} title="Discount codes" testid="discount-codes-card"
          actions={<span className="text-xs text-zinc-400">Machine-wide · applies to every cert unless scoped</span>}>
      <div className="grid grid-cols-[1.1fr_0.6fr_1.4fr_1.2fr_auto_auto] gap-2 text-[11px] font-bold text-zinc-500 uppercase tracking-wide mb-1 px-1">
        <span>Code</span><span>$ off</span><span>Stripe coupon ID</span><span>Scope</span><span>Active</span><span />
      </div>
      {codes.map((c, i) => (
        <div key={c.id || i} className="grid grid-cols-[1.1fr_0.6fr_1.4fr_1.2fr_auto_auto] gap-2 items-center mb-1.5" data-testid={`code-row-${i}`}>
          <input data-testid={`code-string-${i}`} className={`${input} font-mono`} placeholder="RAY20" value={c.code} onChange={(e) => edit(i, { code: e.target.value.toUpperCase() })} />
          <input data-testid={`code-amount-${i}`} type="number" className={input} value={c.amount_usd} onChange={(e) => edit(i, { amount_usd: e.target.value })} />
          <input data-testid={`code-coupon-${i}`} className={`${input} font-mono`} placeholder="auto if blank" value={c.stripe_coupon_id || ""} onChange={(e) => edit(i, { stripe_coupon_id: e.target.value })} />
          <select data-testid={`code-scope-${i}`} className={input} value={c.cert_slug || ""} onChange={(e) => edit(i, { cert_slug: e.target.value || null })}>
            <option value="">All certs</option>
            {certs.map((x) => <option key={x.slug} value={x.slug}>{x.name}</option>)}
          </select>
          <input data-testid={`code-active-${i}`} type="checkbox" className="h-4 w-4 justify-self-center" checked={!!c.active} onChange={(e) => edit(i, { active: e.target.checked })} />
          <button data-testid={`code-remove-${i}`} className="text-xs text-red-500 hover:underline" onClick={() => setCodes(codes.filter((_, k) => k !== i))}>remove</button>
        </div>
      ))}
      <div className="flex items-center gap-4 mt-3">
        <button data-testid="add-discount-code" className="text-xs font-semibold text-sky-600 hover:underline"
                onClick={() => setCodes([...codes, { code: "", amount_usd: 10, stripe_coupon_id: "", cert_slug: null, active: true }])}>+ add code</button>
        <button data-testid="save-discount-codes" onClick={save} disabled={busy} className="px-3 py-1.5 rounded-md bg-sky-500 text-white text-xs font-semibold hover:bg-sky-600 disabled:opacity-50 transition-colors duration-150">Save codes</button>
        <p className="text-xs text-zinc-400">Typed on a cert page → pre-applied coupon. Left blank → Stripe's own promo box is enabled. Both paths land on the sales chips.</p>
      </div>
    </Card>
  );
}
